import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Layout from '../components/layout/Layout';
import { User, Team, Task } from '../types';
import { usersAPI, teamsAPI, tasksAPI, projectsAPI } from '../services/api';
import LoadingSpinner from '../components/common/LoadingSpinner';

import TeamManagement from '../components/admin/TeamManagement';
import TaskOverview from '../components/admin/TaskOverview';
import AdminCalendar from '../components/admin/AdminCalendar';
import StatsCards from '../components/admin/StatsCards';

type ActiveTab = 'overview' | 'users' | 'teams' | 'projects' | 'calendar';

const AdminDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState<ActiveTab>('overview');
  const [users, setUsers] = useState<User[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setIsLoading(true);
      const [usersData, teamsData, allTasksData, standaloneTasksData, projectsData] = await Promise.all([
        usersAPI.getAll(8, true), // Get 8 most recent users for dashboard
        teamsAPI.getAll(),
        tasksAPI.getAll({ limit: 10, includeProjectTasks: true }), // Get all tasks including project tasks
        tasksAPI.getAll({ limit: 5, includeProjectTasks: false }), // Get standalone tasks only
        projectsAPI.getAll({ limit: 5 }), // Get 5 most recent projects
      ]);
      setUsers(usersData);
      setTeams(teamsData);
      // Handle both old and new API response formats
      if (Array.isArray(allTasksData)) {
        setTasks(allTasksData);
      } else {
        setTasks(allTasksData.data);
      }
      setProjects(projectsData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const tabs = [
    { id: 'overview', name: 'Overview', icon: '📊' },
    { id: 'users', name: 'Users', icon: '👥' },
    { id: 'teams', name: 'Teams', icon: '🏢' },
    { id: 'projects', name: 'Projects', icon: '📁' },
    { id: 'calendar', name: 'Calendar', icon: '📅' },
  ];

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
          <p className="mt-2 text-gray-600">
            Manage users, teams, and tasks across your organization
          </p>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-8">
          <nav className="-mb-px flex flex-wrap gap-x-8 gap-y-2">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as ActiveTab)}
                className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors duration-200 whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <span className="mr-2">{tab.icon}</span>
                <span className="hidden sm:inline">{tab.name}</span>
                <span className="sm:hidden">{tab.name.split(' ')[0]}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          {activeTab === 'overview' && (
            <div className="space-y-8">
              <StatsCards users={users} teams={teams} tasks={tasks} projects={projects} />
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <TaskOverview tasks={tasks} />
                <div className="card">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium text-gray-900">Recent Users</h3>
                    <span className="text-sm text-gray-500">Last 8 created</span>
                  </div>
                  <div className="space-y-3">
                    {users.length === 0 ? (
                      <p className="text-gray-500 text-center py-4">No users yet</p>
                    ) : (
                      users.map((user) => (
                        <div key={user.id} className="flex items-center space-x-3">
                          <div className="flex-shrink-0">
                            <div className="w-8 h-8 bg-primary-600 rounded-full flex items-center justify-center">
                              <span className="text-xs font-medium text-white">
                                {user.firstName[0]}{user.lastName[0]}
                              </span>
                            </div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {user.firstName} {user.lastName}
                            </p>
                            <p className="text-sm text-gray-500">
                              {user.role} • {new Date(user.createdAt).toLocaleDateString()}
                            </p>
                          </div>
                          <div className={`px-2 py-1 text-xs font-medium rounded-full ${
                            user.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {user.isActive ? 'Active' : 'Inactive'}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'users' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Recent Users</h2>
                  <p className="text-gray-600">Latest 8 users created in the system</p>
                </div>
                <button
                  onClick={() => window.location.href = '/admin/users'}
                  className="btn-primary"
                >
                  View All Users
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {users.map((user) => (
                  <div key={user.id} className="card hover:shadow-md transition-shadow">
                    <div className="flex items-center space-x-3 mb-4">
                      <div className="w-12 h-12 bg-primary-600 rounded-full flex items-center justify-center">
                        <span className="text-lg font-bold text-white">
                          {user.firstName[0]}{user.lastName[0]}
                        </span>
                      </div>
                      <div>
                        <h3 className="font-medium text-gray-900">
                          {user.firstName} {user.lastName}
                        </h3>
                        <p className="text-sm text-gray-500">{user.email}</p>
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        user.role === 'ADMIN' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
                      }`}>
                        {user.role}
                      </span>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        user.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {user.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>

                    <div className="mt-3 text-xs text-gray-500">
                      Created {new Date(user.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'teams' && (
            <TeamManagement 
              teams={teams} 
              users={users} 
              onTeamsChange={setTeams} 
            />
          )}

          {activeTab === 'projects' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-white p-6 rounded-lg border border-gray-200">
                  <h3 className="text-sm font-medium text-gray-600">Total Projects</h3>
                  <p className="text-2xl font-bold text-gray-900">{projects.length}</p>
                </div>
                <div className="bg-white p-6 rounded-lg border border-gray-200">
                  <h3 className="text-sm font-medium text-gray-600">Active Projects</h3>
                  <p className="text-2xl font-bold text-gray-900">
                    {projects.filter(p => p.status === 'ACTIVE').length}
                  </p>
                </div>
                <div className="bg-white p-6 rounded-lg border border-gray-200">
                  <h3 className="text-sm font-medium text-gray-600">Completed Projects</h3>
                  <p className="text-2xl font-bold text-gray-900">
                    {projects.filter(p => p.status === 'COMPLETED').length}
                  </p>
                </div>
                <div className="bg-white p-6 rounded-lg border border-gray-200">
                  <h3 className="text-sm font-medium text-gray-600">Total Tasks</h3>
                  <p className="text-2xl font-bold text-gray-900">
                    {projects.reduce((sum, p) => sum + (p._count?.tasks || 0), 0)}
                  </p>
                </div>
              </div>

              <div className="bg-white rounded-lg border border-gray-200">
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Projects</h3>
                  <div className="space-y-4">
                    {projects.slice(0, 5).map((project) => (
                      <div key={project.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                        <div className="flex items-center space-x-4">
                          <div
                            className="w-4 h-4 rounded-full"
                            style={{ backgroundColor: project.color }}
                          />
                          <div>
                            <h4 className="font-medium text-gray-900">{project.name}</h4>
                            <p className="text-sm text-gray-600">{project.description}</p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-4">
                          <span className="text-sm text-gray-500">
                            {project._count?.tasks || 0} tasks
                          </span>
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                            project.status === 'ACTIVE' ? 'bg-blue-100 text-blue-800' :
                            project.status === 'COMPLETED' ? 'bg-green-100 text-green-800' :
                            project.status === 'ON_HOLD' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {project.status.replace('_', ' ')}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'calendar' && (
            <AdminCalendar tasks={tasks} />
          )}
        </motion.div>
      </div>
    </Layout>
  );
};

export default AdminDashboard;
